import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { format } from 'date-fns';
import { ArrowLeft } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { PhoebeChatDebugEventTimeline } from '@apps/web/components/phoebe_chat_debug_event_timeline';
import { PhoebeChatDebugRunStats } from '@apps/web/components/phoebe_chat_debug_sidebar';
import { buildPhoebeChatDebugSnapshot } from '@apps/web/components/phoebe_chat_debug_snapshot';
import {
    PhoebeAgentEventFeedChatSurface,
    usePhoebeAgentEventFeedChatViewState,
} from '@apps/web/components/phoebe_chat_view';
import { IdentifierRedactionProvider } from '@apps/web/components/phoebe_chat_view/tool_calls/tool_call_template';
import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import { Spinner } from '@apps/web/components/ui/spinner';
import { TabsContent } from '@apps/web/components/ui/tabs';
import { usePhoebeAgentRunEventStream } from '@apps/web/hooks/use_phoebe_agent_run_event_stream';
import { cn } from '@apps/web/lib/utils';
import {
    AgentTracePrimaryTabs,
    RUN_AGENT_TRACE_PRIMARY_TABS,
} from '@apps/web/routes/_app/admin/agent-traces/agent_trace_primary_tabs';
import {
    RelatedRunLink,
    SubagentRunsPanel,
} from '@apps/web/routes/_app/admin/agent-traces/agent_trace_related_runs';
import { BraintrustExecutionLinks } from '@apps/web/routes/_app/admin/agent-traces/braintrust_execution_links';
import {
    useAdminAgentRun,
    useAdminAgentRunChildren,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';
import {
    type AgentTraceChildRunRead,
    type AgentTraceRunCursorRead,
} from '@libraries/typescript/api_client';

const CHILDREN_PAGE_SIZE = 20;
const RUN_REFRESH_INTERVAL_MS = 3_000;
const DETAIL_PANEL_HEIGHT_CLASS = 'min-h-[36rem] lg:h-full lg:min-h-0';
const SCROLL_REGION_CLASS_NAME = 'min-h-0 overflow-y-auto';

export const Route = createFileRoute('/_app/admin/agent-traces/run/$runId')({
    component: AdminAgentRunTraceRoute,
    head: () => ({ meta: [{ title: 'Agent Run Trace' }] }),
});

function AdminAgentRunTraceRoute() {
    const { runId } = Route.useParams();
    return <AdminAgentRunTracePage runId={runId} />;
}

function mergeChildrenByRunId(
    firstPageChildren: Array<AgentTraceChildRunRead>,
    olderChildren: Array<AgentTraceChildRunRead>,
): Array<AgentTraceChildRunRead> {
    const firstPageRunIds = new Set(
        firstPageChildren.map((child) => child.run_id),
    );
    return [
        ...firstPageChildren,
        ...olderChildren.filter((child) => !firstPageRunIds.has(child.run_id)),
    ];
}

function useBusyRunRefresh({
    isBusy,
    refreshChildren,
    refreshRun,
    runId,
}: {
    readonly isBusy: boolean;
    readonly refreshChildren: () => Promise<unknown>;
    readonly refreshRun: () => Promise<unknown>;
    readonly runId: string;
}) {
    const previousRunStateRef = useRef({ isBusy: false, runId });

    useEffect(() => {
        const wasBusy =
            previousRunStateRef.current.runId === runId &&
            previousRunStateRef.current.isBusy;
        previousRunStateRef.current = { isBusy, runId };

        if (!isBusy) {
            if (wasBusy) {
                void Promise.all([refreshRun(), refreshChildren()]);
            }
            return;
        }

        const intervalId = window.setInterval(() => {
            void Promise.all([refreshRun(), refreshChildren()]);
        }, RUN_REFRESH_INTERVAL_MS);
        return () => window.clearInterval(intervalId);
    }, [isBusy, refreshChildren, refreshRun, runId]);
}

function RunRenderedEventFeed({
    canOpenSubagentRun,
    fallbackRunStatus,
    onOpenSubagentRun,
    organizationId,
    runEventStream,
    runId,
}: {
    readonly canOpenSubagentRun: (childRunId: string) => boolean;
    readonly fallbackRunStatus: string;
    readonly onOpenSubagentRun: (childRunId: string) => void;
    readonly organizationId: string;
    readonly runEventStream: ReturnType<typeof usePhoebeAgentRunEventStream>;
    readonly runId: string;
}) {
    const chatState = usePhoebeAgentEventFeedChatViewState({
        events: runEventStream.events,
        pendingEvents: runEventStream.pendingEvents,
        fallbackRunStatus: runEventStream.runState?.status ?? fallbackRunStatus,
        phoebeAgentRunId: runId,
        showGeneralOutreachProgressCards: true,
        showInlineDebugEvents: true,
        liveEventIds: runEventStream.liveEventIds,
    });
    const hasSnapshot =
        runEventStream.runState !== null ||
        runEventStream.events.length > 0 ||
        runEventStream.pendingEvents.length > 0;

    if (runEventStream.isLoading && !hasSnapshot) {
        return (
            <div className="text-muted-foreground flex h-full items-center justify-center gap-2 text-sm">
                <Spinner size="sm" />
                Loading events…
            </div>
        );
    }

    if (runEventStream.error && !hasSnapshot) {
        return (
            <div className="text-muted-foreground flex h-full items-center justify-center text-sm">
                Couldn’t load run events. Retrying…
            </div>
        );
    }

    return (
        <div className="flex h-full min-h-0 flex-col">
            {runEventStream.connectionStatus === 'reconnecting' ||
            runEventStream.connectionStatus === 'error' ? (
                <div className="text-muted-foreground border-border flex h-9 shrink-0 items-center gap-2 border-b px-3 text-xs">
                    <Spinner size="xs" />
                    Reconnecting…
                </div>
            ) : null}
            <div className="min-h-0 flex-1">
                <IdentifierRedactionProvider redact={false}>
                    <PhoebeAgentEventFeedChatSurface
                        state={chatState}
                        canOpenSubagentRun={canOpenSubagentRun}
                        onSubmit={() => false}
                        readOnly={true}
                        organizationId={organizationId}
                        emptyState="No events yet."
                        showGeneralOutreachProgressCards={true}
                        showRecommendationTraceProgress={true}
                        onOpenSubagentRun={onOpenSubagentRun}
                    />
                </IdentifierRedactionProvider>
            </div>
        </div>
    );
}

export function AdminAgentRunTracePage({ runId }: { readonly runId: string }) {
    const navigate = useNavigate();
    const {
        data: run,
        error: runError,
        isLoading: isLoadingRun,
        mutate: refreshRun,
    } = useAdminAgentRun(runId);
    const {
        data: firstChildrenPage,
        error: childrenError,
        mutate: refreshFirstChildrenPage,
    } = useAdminAgentRunChildren(runId, {
        limit: CHILDREN_PAGE_SIZE,
    });
    const [loadMoreCursor, setLoadMoreCursor] =
        useState<AgentTraceRunCursorRead | null>(null);
    const [olderChildren, setOlderChildren] = useState<
        Array<AgentTraceChildRunRead>
    >([]);
    const [olderNextCursor, setOlderNextCursor] = useState<
        AgentTraceRunCursorRead | null | undefined
    >(undefined);
    const [loadMoreFailed, setLoadMoreFailed] = useState(false);
    const processedLoadMoreCursorRef = useRef<string | null>(null);
    const {
        data: loadMorePage,
        error: loadMoreError,
        isLoading: isLoadingMore,
        isValidating: isValidatingMore,
    } = useAdminAgentRunChildren(runId, {
        cursor: loadMoreCursor,
        enabled: loadMoreCursor !== null,
        limit: CHILDREN_PAGE_SIZE,
    });
    const runEventStream = usePhoebeAgentRunEventStream({
        organizationId: run?.organization_id ?? null,
        phoebeAgentRunId: runId,
        enabled: run?.agent_conversation_id === null,
        mode: run?.mode,
        variant: 'admin',
    });

    useEffect(() => {
        setLoadMoreCursor(null);
        setOlderChildren([]);
        setOlderNextCursor(undefined);
        setLoadMoreFailed(false);
        processedLoadMoreCursorRef.current = null;
    }, [runId]);

    useEffect(() => {
        if (!loadMoreCursor || !loadMorePage) {
            return;
        }
        const cursorKey = `${loadMoreCursor.created_at}:${loadMoreCursor.run_id}`;
        if (processedLoadMoreCursorRef.current === cursorKey) {
            return;
        }
        processedLoadMoreCursorRef.current = cursorKey;
        setOlderChildren((current) =>
            mergeChildrenByRunId(current, loadMorePage.children),
        );
        setOlderNextCursor(loadMorePage.next_cursor);
        setLoadMoreFailed(false);
        setLoadMoreCursor(null);
    }, [loadMoreCursor, loadMorePage]);

    useEffect(() => {
        if (!loadMoreCursor || !loadMoreError) {
            return;
        }
        setLoadMoreFailed(true);
        setLoadMoreCursor(null);
    }, [loadMoreCursor, loadMoreError]);

    useEffect(() => {
        if (!run?.agent_conversation_id) {
            return;
        }
        void navigate({
            to: '/admin/agent-traces/$conversationId',
            params: { conversationId: run.agent_conversation_id },
            replace: true,
        });
    }, [navigate, run?.agent_conversation_id]);

    useBusyRunRefresh({
        isBusy: runEventStream.isBusy,
        refreshChildren: refreshFirstChildrenPage,
        refreshRun,
        runId,
    });

    const children = useMemo(
        () =>
            mergeChildrenByRunId(
                firstChildrenPage?.children ?? [],
                olderChildren,
            ),
        [firstChildrenPage?.children, olderChildren],
    );
    const nextCursor =
        olderNextCursor === undefined
            ? (firstChildrenPage?.next_cursor ?? null)
            : olderNextCursor;
    const handleOpenSubagentRun = useCallback(
        (childRunId: string) => {
            const childRun = children.find(
                (candidate) => candidate.run_id === childRunId,
            );
            if (!childRun) {
                return;
            }
            if (childRun.conversation_id) {
                void navigate({
                    to: '/admin/agent-traces/$conversationId',
                    params: { conversationId: childRun.conversation_id },
                });
                return;
            }
            void navigate({
                to: '/admin/agent-traces/run/$runId',
                params: { runId: childRun.run_id },
            });
        },
        [children, navigate],
    );
    const canOpenSubagentRun = useCallback(
        (childRunId: string) =>
            children.some((child) => child.run_id === childRunId),
        [children],
    );

    if (isLoadingRun) {
        return (
            <div className="flex items-center justify-center py-12">
                <Spinner size="xl" tone="muted" />
            </div>
        );
    }

    if (runError || !run) {
        return (
            <div className="text-muted-foreground py-12 text-center text-sm">
                Run not found.
            </div>
        );
    }

    if (run.agent_conversation_id) {
        return null;
    }

    const displayStatus = runEventStream.runState?.status ?? run.status;
    const debugSnapshot = buildPhoebeChatDebugSnapshot({
        conversationId: null,
        conversationTitle:
            run.title ?? run.organization_name ?? `Run ${run.id.slice(0, 8)}`,
        phoebeRunId: run.id,
        agentGeneration: null,
        eventReadEnabled: true,
        connectionStatus: runEventStream.connectionStatus,
        runState: runEventStream.runState,
        events: runEventStream.events,
        pendingEvents: runEventStream.pendingEvents,
    });

    return (
        <div className="flex min-w-0 gap-4 p-6 lg:h-svh lg:max-h-svh lg:min-h-0 lg:overflow-hidden">
            <div className="flex min-w-0 flex-1 flex-col gap-4 lg:min-h-0 lg:overflow-y-auto">
                <div className="flex shrink-0 items-center gap-3">
                    <Button asChild={true} variant="ghost" size="sm">
                        <Link
                            to="/admin/agent-traces"
                            aria-label="Back to agent traces"
                        >
                            <ArrowLeft className="size-4" />
                        </Link>
                    </Button>
                    <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                            <h1 className="truncate text-lg font-semibold">
                                {run.title ?? `Run ${run.id.slice(0, 8)}`}
                            </h1>
                            <Badge variant="secondary">
                                {run.run_type.replace('_', ' ')}
                            </Badge>
                            <Badge variant="outline">
                                {displayStatus.replace('_', ' ')}
                            </Badge>
                        </div>
                        <div className="text-muted-foreground flex flex-wrap items-center gap-3 text-xs">
                            <span>
                                {run.organization_name ?? run.organization_id}
                            </span>
                            <span>
                                Created{' '}
                                {format(
                                    new Date(run.created_at),
                                    'MMM d, h:mm a',
                                )}
                            </span>
                            {run.client_surface ? (
                                <span>{run.client_surface}</span>
                            ) : null}
                            {run.parent_run ? (
                                <RelatedRunLink
                                    run={run.parent_run}
                                    label="Parent run"
                                    variant="inline"
                                />
                            ) : null}
                            {children.length > 0 ? (
                                <Badge variant="secondary">
                                    {children.length} subagent
                                    {children.length === 1 ? '' : 's'}
                                </Badge>
                            ) : null}
                        </div>
                    </div>
                    <BraintrustExecutionLinks
                        executions={run.executions}
                        runBraintrustUrl={run.braintrust_run_url}
                    />
                </div>
                <AgentTracePrimaryTabs tabs={RUN_AGENT_TRACE_PRIMARY_TABS}>
                    <TabsContent
                        value="rendered-chat"
                        className="pt-2 lg:h-full lg:min-h-0 lg:overflow-hidden"
                    >
                        <div
                            className={cn(
                                'bg-card overflow-hidden rounded-xl border',
                                DETAIL_PANEL_HEIGHT_CLASS,
                            )}
                        >
                            <RunRenderedEventFeed
                                canOpenSubagentRun={canOpenSubagentRun}
                                fallbackRunStatus={run.status}
                                onOpenSubagentRun={handleOpenSubagentRun}
                                organizationId={run.organization_id}
                                runEventStream={runEventStream}
                                runId={run.id}
                            />
                        </div>
                    </TabsContent>
                    <TabsContent
                        value="events"
                        className="pt-2 lg:h-full lg:min-h-0 lg:overflow-hidden"
                    >
                        <div
                            className={cn(
                                'bg-card grid overflow-hidden rounded-xl border lg:grid-cols-[minmax(0,1fr)_280px]',
                                DETAIL_PANEL_HEIGHT_CLASS,
                            )}
                        >
                            <div className="min-h-0 overflow-hidden lg:border-r">
                                <PhoebeChatDebugEventTimeline
                                    snapshot={debugSnapshot}
                                />
                            </div>
                            <div className="hidden min-h-0 overflow-hidden lg:block">
                                <PhoebeChatDebugRunStats
                                    snapshot={debugSnapshot}
                                />
                            </div>
                        </div>
                    </TabsContent>
                    <TabsContent
                        value="subagents"
                        className="pt-2 lg:h-full lg:min-h-0 lg:overflow-hidden"
                    >
                        <div
                            className={cn(
                                'space-y-3 pr-1',
                                SCROLL_REGION_CLASS_NAME,
                                DETAIL_PANEL_HEIGHT_CLASS,
                            )}
                        >
                            {childrenError ? (
                                <div className="text-destructive bg-card rounded-xl border p-4 text-sm">
                                    Couldn’t load related runs.
                                </div>
                            ) : null}
                            {loadMoreFailed ? (
                                <div className="text-destructive text-center text-sm">
                                    Couldn’t load more related runs.
                                </div>
                            ) : null}
                            <SubagentRunsPanel
                                parentRun={run.parent_run}
                                childRuns={children}
                                runType={run.run_type}
                            />
                            {nextCursor ? (
                                <div className="flex justify-center">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        disabled={
                                            loadMoreCursor !== null ||
                                            isLoadingMore ||
                                            isValidatingMore
                                        }
                                        onClick={() => {
                                            setLoadMoreFailed(false);
                                            setLoadMoreCursor(nextCursor);
                                        }}
                                    >
                                        {loadMoreCursor !== null ||
                                        isLoadingMore ||
                                        isValidatingMore ? (
                                            <Spinner size="sm" />
                                        ) : null}
                                        {loadMoreFailed
                                            ? 'Try Again'
                                            : 'Load More'}
                                    </Button>
                                </div>
                            ) : null}
                        </div>
                    </TabsContent>
                </AgentTracePrimaryTabs>
            </div>
        </div>
    );
}
